import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquare, Pin, Search } from 'lucide-react';

import { FIREBASE_CONFIG, LAW_FIRM_INFO } from '../config/Config';
import { subscribeActivityFeed, toggleEventPinned, addEventReply, fetchDocumentMeta, fetchClientMeta } from './GlobalActivityFeedRepository';
import { requestDocumentConsent } from '../utils/documentConsent';
import { resolveDocumentUrl } from '../utils/documentResolver';
import { queuePublishedDocumentEdit, resolvePublishedDocumentEditTarget } from '../utils/publishedDocumentEditor';
import { buildLatestPublicationVersionBySeries, buildPublicationVersionState, buildPublishedDocumentActionState } from '../utils/prontuarioPublicationSSOT';
import { useClientFlyout } from '../hooks/useClientFlyout';
import { FlyoutPanel } from '../components/common/FlyoutPanel';
import { toTitleCasePt } from '../utils/formatters';

const appId = FIREBASE_CONFIG.projectId;

const normalizeWorkspaceLabel = (label) => {
  const raw = String(label || '').trim();
  if (!raw) return '';
  const head = raw.split('•')[0];
  return String(head || '').trim();
};

const titleCaseWords = (value) => String(value || '')
  .split(/\s+/)
  .filter(Boolean)
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
  .join(' ');

export default function GlobalActivityFeed({ user, extMacroFilter = null, onExtMacroFilterChange = null, extEventTypeFilter = null, onExtEventTypeFilterChange = null, extSearchTerm = null, onExtSearchTermChange = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [indexUrl, setIndexUrl] = useState('');

  const [macroFilterInternal, setMacroFilterInternal] = useState('ALL');
  const macroFilter = extMacroFilter !== null ? extMacroFilter : macroFilterInternal;
  const setMacroFilter = (v) => { setMacroFilterInternal(v); if (onExtMacroFilterChange) onExtMacroFilterChange(v); };
  const [eventTypeFilterInternal, setEventTypeFilterInternal] = useState('ALL');
  const eventTypeFilter = extEventTypeFilter !== null ? extEventTypeFilter : eventTypeFilterInternal;
  const setEventTypeFilter = (v) => { setEventTypeFilterInternal(v); if (onExtEventTypeFilterChange) onExtEventTypeFilterChange(v); };
  const [searchTermInternal, setSearchTermInternal] = useState('');
  const searchTerm = extSearchTerm !== null ? extSearchTerm : searchTermInternal;
  const setSearchTerm = (v) => { setSearchTermInternal(v); if (onExtSearchTermChange) onExtSearchTermChange(v); };
  const [sortKey, setSortKey] = useState('DT');
  const [sortDir, setSortDir] = useState('desc');
  const flyout = useClientFlyout();
  const [replyDraft, setReplyDraft] = useState('');
  const [replySaving, setReplySaving] = useState(false);
  const [resolvedEventUrl, setResolvedEventUrl] = useState('');
  const [resolvingEventUrl, setResolvingEventUrl] = useState(false);

  const [clientLabels, setClientLabels] = useState({});
  const [clientLabelsReady, setClientLabelsReady] = useState(false);
  const [documentMetaById, setDocumentMetaById] = useState({});
  const clientLabelsRef = useRef({});
  const documentMetaRef = useRef({});
  const clientLoadBusyRef = useRef(false);
  const documentLoadBusyRef = useRef(false);
  const replyTextareaRef = useRef(null);

  useEffect(() => {
    if (!flyout.flyoutOpen) return;

    const onKeyDownCapture = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        flyout.setFlyoutOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDownCapture, true);
    return () => window.removeEventListener('keydown', onKeyDownCapture, true);
  }, [flyout.flyoutOpen]);

  useEffect(() => {
    if (!flyout.flyoutOpen) return;
    const t = setTimeout(() => {
      const el = replyTextareaRef.current;
      if (!el) return;
      if (typeof el.focus !== 'function') return;
      el.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [flyout.flyoutOpen, flyout.selectedDoc]);

  useEffect(() => {
    if (flyout.flyoutOpen) return;
    if (!flyout.selectedDoc) return;
    const t = setTimeout(() => flyout.setSelectedDoc(null), 200);
    return () => clearTimeout(t);
  }, [flyout.flyoutOpen, flyout.selectedDoc]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setIndexUrl('');

    const unsubscribe = subscribeActivityFeed(
      (out) => { setItems(out); setLoading(false); setError(''); setIndexUrl(''); },
      ({ type, url, msg }) => {
        if (type === 'index') {
          setIndexUrl(url); setError(''); setLoading(true);
        } else {
          setError(msg); setIndexUrl(url); setLoading(false);
        }
      }
    );

    return () => { try { unsubscribe(); } catch (e) { void e; } };
  }, []);

  const formatGridDt = useCallback((dt) => {
    try {
      if (!dt) return '';
      const date = dt.toLocaleDateString('pt-BR');
      const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      return `${date} ${time}`;
    } catch {
      return '';
    }
  }, []);

  const normalizeEvent = useCallback((row) => {
    const raw = row || {};
    const categoryRaw = String(raw?.category || '').trim().toUpperCase();
    const category = categoryRaw === 'SYSTEM' ? 'SYSTEM' : categoryRaw === 'USER' ? 'USER' : '';
    const type = String(raw?.type || '').trim() || '';
    const payload = raw?.payload && typeof raw.payload === 'object' ? raw.payload : {};
    const actor = raw?.actor && typeof raw.actor === 'object' ? raw.actor : {};

    if (category && actor && (payload || type)) {
      const rawActorName = String(actor?.name || '').trim();
      const actorName = rawActorName || (category === 'SYSTEM' ? 'Sistema' : 'Operador');
      const actorIsBot = Boolean(actor?.isBot);
      return {
        category,
        type,
        payload,
        actor: { name: actorName, isBot: actorIsBot },
      };
    }

    const legacyText = String(raw?.text || '').trim();
    const legacyType = String(raw?.type || '').trim().toUpperCase();
    const legacyAuthor = String(raw?.author || '').trim();

    if (legacyType === 'SYSTEM_DOC') {
      const prefix = 'Documento anexado:';
      const inferredFileName = legacyText.toLowerCase().startsWith(prefix.toLowerCase())
        ? legacyText.slice(prefix.length).trim()
        : '';

      return {
        category: 'SYSTEM',
        type: 'DOC_UPLOADED',
        payload: {
          fileName: inferredFileName || undefined,
          text: legacyText || undefined,
        },
        actor: { name: legacyAuthor || 'Sistema', isBot: true },
      };
    }

    return {
      category: 'USER',
      type: 'USER_NOTE_ADDED',
      payload: { text: legacyText || undefined },
      actor: { name: legacyAuthor || 'Operador', isBot: false },
    };
  }, []);

  const humanizeActorName = useCallback((actorName, category) => {
    const raw = String(actorName || '').trim();
    if (!raw) return category === 'SYSTEM' ? 'Sistema' : 'Operador';

    const userEmail = String(user?.email || '').trim().toLowerCase();
    const userDisplayName = String(user?.displayName || '').trim();
    const officeEmail = String(LAW_FIRM_INFO?.contact?.email || '').trim().toLowerCase();
    const officeName = String(LAW_FIRM_INFO?.name || '').trim();
    const officeFirstName = String(officeName.split(/\s+/)[0] || '').trim().toLowerCase();
    const rawLower = raw.toLowerCase();

    if (rawLower === 'operador') return userDisplayName || officeName || 'Operador';
    if (rawLower === 'sistema') return 'Sistema';
    if (userEmail && rawLower === userEmail) return userDisplayName || officeName || raw;
    if (officeEmail && rawLower === officeEmail) return userDisplayName || officeName || raw;
    if (officeFirstName && rawLower === officeFirstName) return officeName || raw;

    if (raw.includes('@')) {
      const emailUser = String(raw.split('@')[0] || '').trim();
      if (emailUser && userDisplayName && userEmail && rawLower === userEmail) return userDisplayName;
      if (emailUser && officeEmail && rawLower === officeEmail) return officeName || titleCaseWords(emailUser.replace(/[._-]+/g, ' '));
      if (emailUser && officeFirstName && String(emailUser || '').trim().toLowerCase() === officeFirstName) return officeName || titleCaseWords(emailUser.replace(/[._-]+/g, ' '));
      if (emailUser) return titleCaseWords(emailUser.replace(/[._-]+/g, ' '));
    }

    return raw;
  }, [user]);

  const resolveTypeDetail = useCallback(
    (event) => {
      const cat = String(event?.category || '').trim().toUpperCase();
      if (cat !== 'SYSTEM') return '';
      const t = String(event?.type || '').trim().toUpperCase();
      if (!t) return '';
      if (t === 'DOC_UPLOADED') return '';
      if (t === 'DOCUMENT_PUBLISHED') return '';
      if (t === 'USER_NOTE_ADDED') return '';
      if (t === 'SYSTEM') return '';
      return String(event?.type || '').trim();
    },
    []
  );

  const escapeHtml = useCallback((v) => {
    const s = String(v ?? '');
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }, []);

  const legacyTextToHtml = useCallback(
    (v) => {
      const s = String(v ?? '').trim();
      if (!s) return '';
      const safe = escapeHtml(s).replace(/\n/g, '<br />');
      return `<p>${safe}</p>`;
    },
    [escapeHtml]
  );

  const sanitizeHtml = useCallback(
    (html) => {
      const raw = String(html ?? '').trim();
      if (!raw) return '';
      if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return legacyTextToHtml(raw);
      }

      const allowedTags = new Set(['P', 'BR', 'STRONG', 'B', 'EM', 'I', 'UL', 'OL', 'LI', 'DIV', 'SPAN']);
      const parser = new window.DOMParser();
      const doc = parser.parseFromString(`<div>${raw}</div>`, 'text/html');
      const root = doc.body?.firstElementChild;
      if (!root) return '';

      const walk = (node) => {
        if (!node) return;

        const children = Array.from(node.childNodes || []);
        children.forEach((child) => {
          if (child.nodeType === 1) {
            const el = child;
            const tag = String(el.tagName || '').toUpperCase();

            if (!allowedTags.has(tag)) {
              const text = doc.createTextNode(el.textContent || '');
              el.replaceWith(text);
              return;
            }

            Array.from(el.attributes || []).forEach((a) => {
              try {
                el.removeAttribute(a.name);
              } catch (e) {
                void e;
              }
            });

            walk(el);
          } else if (child.nodeType === 8) {
            try {
              child.remove();
            } catch (e) {
              void e;
            }
          }
        });
      };

      walk(root);

      const out = String(root.innerHTML || '').trim();
      return out;
    },
    [legacyTextToHtml]
  );

  const resolvePayloadHtml = useCallback(
    (event) => {
      const payload = event?.payload && typeof event.payload === 'object' ? event.payload : {};

      const rawHtml = String(payload?.html || '').trim();
      if (rawHtml) return rawHtml;

      const rawText = String(payload?.text || '').trim();
      if (!rawText) return '';

      const looksLikeHtml = rawText.includes('<') && rawText.includes('>');
      return looksLikeHtml ? rawText : legacyTextToHtml(rawText);
    },
    [legacyTextToHtml]
  );

  const norm = useCallback((v) => {
    return String(v || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }, []);

  const myAliases = useMemo(() => {
    const email = String(user?.email || '').trim();
    const emailUser = email && email.includes('@') ? email.split('@')[0] : '';
    const displayName = String(user?.displayName || '').trim();
    return [displayName, email, emailUser].filter(Boolean).map((v) => norm(v));
  }, [user, norm]);

  const isMine = useCallback(
    (actorName) => {
      const a = norm(actorName);
      if (!a) return false;
      if (!Array.isArray(myAliases) || myAliases.length === 0) return false;
      return myAliases.some((x) => x && (a === x || a.includes(x)));
    },
    [myAliases, norm]
  );

  const gridRows = useMemo(() => {
    const base = (Array.isArray(items) ? items : []).map((row) => {
      const rid = String(row?.id || '').trim();
      const dt = row?.createdAt?.toDate ? row.createdAt.toDate() : null;
      const when = dt ? formatGridDt(dt) : '';

      const event = normalizeEvent(row);
      const isSystem = event?.category === 'SYSTEM';

      const eventTypeUpper = String(event?.type || '').trim().toUpperCase();
      const isDocUploaded = eventTypeUpper === 'DOC_UPLOADED';
      const isDocumentPublished = eventTypeUpper === 'DOCUMENT_PUBLISHED';

      const rawActorName = String(event?.actor?.name || '').trim();
      const who = (isDocUploaded && rawActorName.toLowerCase() === 'sistema')
        ? (humanizeActorName('Operador', 'SYSTEM') || '—')
        : humanizeActorName(rawActorName, event?.category) || '—';

      const payloadText = String(event?.payload?.text || '').trim();
      const payloadFileName = String(event?.payload?.fileName || '').trim();
      const payloadFileUrl = String(event?.payload?.fileUrl || event?.payload?.downloadUrl || '').trim();
      const payloadDocumentId = String(event?.payload?.documentId || '').trim();
      const payloadWorkspaceId = String(event?.payload?.workspaceId || '').trim();
      const payloadWorkspaceLabel = String(event?.payload?.workspaceLabel || '').trim();
      const resolvedWorkspaceLabel =
        normalizeWorkspaceLabel(payloadWorkspaceLabel) ||
        (payloadWorkspaceId ? payloadWorkspaceId : (isDocUploaded ? 'Vinculação de Documento' : 'Consulta Avulsa'));

      const clientId = (() => {
        try {
          const parentDoc = row?.__ref?.parent?.parent;
          const cid = parentDoc?.id ? String(parentDoc.id).trim() : '';
          return cid;
        } catch {
          return '';
        }
      })();

      const documentMeta = payloadDocumentId && documentMetaRef.current ? documentMetaRef.current[payloadDocumentId] : null;
      const canonicalClientId = String(documentMeta?.clientId || '').trim() || clientId;
      const clientLabel = canonicalClientId && clientLabelsRef.current ? clientLabelsRef.current[canonicalClientId] : '';
      const canonicalTitle = String(documentMeta?.title || '').trim();
      const publicationSeriesId = String(documentMeta?.publicationSeriesId || payloadDocumentId || '').trim();
      const publicationVersion = Math.max(0, Number(documentMeta?.publicationVersion || event?.payload?.publicationVersion || 0) || 0);

      const typeLabel = (() => {
        if (eventTypeUpper === 'USER_NOTE_ADDED') return 'Anotação do Advogado';
        if (eventTypeUpper === 'PORTAL_UPLOAD_SUBMITTED') return 'Envio do Cliente';
        if (eventTypeUpper === 'CLIENT_DOCUMENT_VIEWED') return 'Leitura do Cliente';
        if (eventTypeUpper === 'PORTAL_UPLOAD_APPROVED') return 'Arquivo Aprovado';
        if (eventTypeUpper === 'PORTAL_UPLOAD_REJECTED') return 'Arquivo Devolvido';
        if (eventTypeUpper === 'CLIENT_CONSENT_ACCEPTED') return 'Consentimento Confirmado';
        if (eventTypeUpper === 'CLIENT_CONSENT_DECLINED') return 'Consentimento Recusado';
        if (eventTypeUpper === 'CLIENT_PORTAL_SESSION_STARTED') return 'Portal Acessado';
        if (eventTypeUpper === 'CLIENT_PORTAL_PRONTUARIO_VIEWED') return 'Prontuário Consultado';
        if (eventTypeUpper === 'CLIENT_PORTAL_PROCESSES_VIEWED') return 'Processos Consultados';
        if (eventTypeUpper === 'CLIENT_PORTAL_FINANCE_VIEWED') return 'Financeiro Consultado';
        if (eventTypeUpper === 'CLIENT_PORTAL_PROFILE_VIEWED') return 'Perfil Consultado';
        if (eventTypeUpper === 'CLIENT_PORTAL_VAULT_VIEWED') return 'Acervo Consultado';
        if (eventTypeUpper === 'PROCESS_CREATED') return 'Processo Criado';
        if (eventTypeUpper === 'PROCESS_UPDATED') return 'Processo Atualizado';
        if (eventTypeUpper === 'PROCESS_STATUS_UPDATED') return 'Status do Processo';
        if (eventTypeUpper === 'PROCESS_MOVEMENT_UPDATED') return 'Movimentação Processual';
        if (eventTypeUpper === 'PROCESS_MOVEMENT_ADDED') return 'Andamento Adicionado';
        if (eventTypeUpper === 'PROCESS_MOVEMENT_EDITED') return 'Andamento Atualizado';
        if (eventTypeUpper === 'FINANCIAL_INVOICE_CREATED') return 'Fatura Disponibilizada';
        if (eventTypeUpper === 'FINANCIAL_ENTRY_POSTED') return 'Lançamento Financeiro';
        if (eventTypeUpper === 'FINANCIAL_ENTRY_UPDATED') return 'Financeiro Atualizado';
        if (eventTypeUpper === 'FINANCIAL_ENTRY_REMOVED') return 'Financeiro Removido';
        if (eventTypeUpper === 'CLIENT_FINANCIAL_PIX_COPIED') return 'Pix Copiado';
        if (eventTypeUpper === 'CLIENT_PORTAL_PAYMENT_SIMULATED') return 'Pagamento Registrado';
        if (eventTypeUpper === 'DOCUMENT_CONSENT_REQUESTED') return 'Consentimento Solicitado';
        if (isDocumentPublished) return 'Documento Publicado';
        if (isDocUploaded) return 'Documento Anexado';
        if (!isSystem) return 'Anotação';
        return 'Sistema';
      })();

      const safeUserHtml = isSystem ? '' : sanitizeHtml(resolvePayloadHtml(event) || payloadText);

      const replies = Array.isArray(row?.replies) ? row.replies : [];
      const repliesCount = replies.length;

      return {
        id: rid,
        __ref: row?.__ref,
        dt,
        when,
        event,
        who,
        isSystem,
        isDocUploaded,
        isDocumentPublished,
        typeLabel,
        typeUpper: eventTypeUpper,
        safeUserHtml,
        fileName: payloadFileName || canonicalTitle,
        fileUrl: payloadFileUrl,
        documentId: payloadDocumentId,
        publicationSeriesId,
        publicationVersion,
        systemText: payloadText,
        isPinned: Boolean(row?.isPinned),
        replies,
        repliesCount,
        workspaceId: payloadWorkspaceId,
        workspaceLabel: resolvedWorkspaceLabel,
        rawClientId: clientId,
        clientId: canonicalClientId,
        clientLabel,
      };
    });

    let out = base;

    if (macroFilter === 'SYSTEM') {
      out = out.filter((r) => r?.isSystem);
    } else if (macroFilter === 'MINE') {
      out = out.filter((r) => !r?.isSystem && isMine(r?.who));
    }

    const typeFilter = String(eventTypeFilter || 'ALL').toUpperCase();
    if (typeFilter === 'DOCS') {
      out = out.filter((r) => Boolean(r?.isDocUploaded));
    } else if (typeFilter === 'NOTES') {
      out = out.filter((r) => !r?.isSystem);
    } else if (typeFilter === 'SYSTEM') {
      out = out.filter((r) => Boolean(r?.isSystem));
    }

    const needle = String(searchTerm || '').trim().toLowerCase();
    if (needle) {
      out = out.filter((r) => {
        const resolvedClientLabel =
          String(r?.clientLabel || clientLabels[String(r?.clientId || '').trim()] || '').trim() || String(r?.clientId || '').trim();

        const blob = [
          resolvedClientLabel,
          r?.workspaceLabel,
          r?.who,
          r?.safeUserHtml,
          r?.fileName,
          r?.systemText,
        ]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');

        return blob.includes(needle);
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    const key = String(sortKey || 'DT');

    const safeLower = (v) => String(v || '').toLowerCase().trim();

    const sorter = (a, b) => {
      if (key === 'DT') {
        const av = a?.dt instanceof Date ? a.dt.getTime() : 0;
        const bv = b?.dt instanceof Date ? b.dt.getTime() : 0;
        return (av - bv) * dir;
      }
      if (key === 'CLIENT') return safeLower(a?.clientLabel || a?.clientId).localeCompare(safeLower(b?.clientLabel || b?.clientId)) * dir;
      if (key === 'WORKSPACE') return safeLower(a?.workspaceLabel).localeCompare(safeLower(b?.workspaceLabel)) * dir;
      if (key === 'TYPE') return safeLower(a?.typeLabel).localeCompare(safeLower(b?.typeLabel)) * dir;
      if (key === 'AUTHOR') return safeLower(a?.who).localeCompare(safeLower(b?.who)) * dir;
      return 0;
    };

    const pinned = out.filter((r) => r?.isPinned).sort(sorter);
    const rest = out.filter((r) => !r?.isPinned).sort(sorter);
    out = [...pinned, ...rest];

    return out;
  }, [items, macroFilter, formatGridDt, normalizeEvent, humanizeActorName, resolvePayloadHtml, sanitizeHtml, sortDir, sortKey, isMine, eventTypeFilter, searchTerm, clientLabels]);

  const latestPublicationVersionBySeries = useMemo(() => {
    return buildLatestPublicationVersionBySeries(Object.values(documentMetaById || {}), gridRows);
  }, [documentMetaById, gridRows]);

  const openPublishedDocument = useCallback((row) => {
    const targetUrl = String(row?.fileUrl || '').trim();
    const documentId = String(row?.documentId || '').trim();
    if (!targetUrl && !documentId) return;

    const title = String(row?.fileName || row?.systemText || row?.typeLabel || 'Documento Publicado').trim() || 'Documento Publicado';
    const when = String(row?.when || '').trim();
    const who = String(row?.who || '').trim();
    const viewerUrl = `/view-published?${new URLSearchParams({
      ...(targetUrl ? { url: targetUrl } : {}),
      ...(documentId ? { documentId } : {}),
      ...(title ? { title } : {}),
      ...(when ? { when } : {}),
      ...(who ? { who } : {}),
    }).toString()}`;

    try {
      const win = window.open(viewerUrl, '_blank', 'noopener,noreferrer');
      if (win) return;
    } catch (e) {
      void e;
    }

    try {
      const win = window.open(viewerUrl, '_blank');
      if (win) return;
    } catch (e2) {
      void e2;
    }
  }, []);

  const myName = useMemo(() => {
    const displayName = String(user?.displayName || '').trim();
    const email = String(user?.email || '').trim();
    const fallback = email && email.includes('@') ? email.split('@')[0] : '';
    return displayName || fallback || email || 'Operador';
  }, [user]);

  const selectedDocumentMeta = useMemo(() => {
    const did = String(flyout.selectedDoc?.documentId || '').trim();
    const base = did ? (documentMetaById?.[did] || {}) : {};
    const pendingByEvent = String(flyout.selectedDoc?.typeUpper || '').trim().toUpperCase() === 'DOCUMENT_CONSENT_REQUESTED';
    return {
      documentId: did,
      clientId: String(base?.clientId || flyout.selectedDoc?.clientId || '').trim(),
      title: String(base?.title || flyout.selectedDoc?.fileName || flyout.selectedDoc?.systemText || '').trim(),
      fileUrl: String(base?.fileUrl || flyout.selectedDoc?.fileUrl || '').trim(),
      url: String(base?.url || flyout.selectedDoc?.url || '').trim(),
      downloadUrl: String(base?.downloadUrl || flyout.selectedDoc?.downloadUrl || '').trim(),
      mirror_url: String(base?.mirror_url || flyout.selectedDoc?.mirror_url || '').trim(),
      storagePath: String(base?.storagePath || flyout.selectedDoc?.storagePath || '').trim(),
      storage_path: String(base?.storage_path || flyout.selectedDoc?.storage_path || '').trim(),
      storageEngine: base?.storageEngine || flyout.selectedDoc?.storageEngine || null,
      publicationSeriesId: String(base?.publicationSeriesId || flyout.selectedDoc?.publicationSeriesId || did).trim(),
      publicationVersion: Math.max(0, Number(base?.publicationVersion || flyout.selectedDoc?.publicationVersion || 0) || 0),
      requiresConsent: base?.requiresConsent ?? pendingByEvent,
      consentStatus: String(base?.consentStatus || (pendingByEvent ? 'pending_consent' : '')).trim(),
      status: String(base?.status || '').trim(),
    };
  }, [documentMetaById, flyout.selectedDoc]);

  const canEditSelectedEvent = useMemo(() => {
    return Boolean(resolvePublishedDocumentEditTarget(flyout.selectedDoc) && String(flyout.selectedDoc?.documentId || '').trim());
  }, [flyout.selectedDoc]);

  const handleEditPublishedDocument = useCallback((row) => {
    const enrichedRow = {
      ...(row || {}),
      documentId: String(selectedDocumentMeta?.documentId || row?.documentId || row?.id || '').trim(),
      publicationSeriesId: String(selectedDocumentMeta?.publicationSeriesId || row?.publicationSeriesId || row?.documentId || row?.id || '').trim(),
      publicationVersion: Math.max(0, Number(selectedDocumentMeta?.publicationVersion || row?.publicationVersion || 0) || 0),
      title: String(selectedDocumentMeta?.title || row?.title || row?.fileName || row?.name || '').trim(),
      fileUrl: String(selectedDocumentMeta?.fileUrl || row?.fileUrl || '').trim(),
      url: String(selectedDocumentMeta?.url || row?.url || '').trim(),
      downloadUrl: String(selectedDocumentMeta?.downloadUrl || row?.downloadUrl || '').trim(),
      mirror_url: String(selectedDocumentMeta?.mirror_url || row?.mirror_url || '').trim(),
      storagePath: String(selectedDocumentMeta?.storagePath || row?.storagePath || '').trim(),
      storage_path: String(selectedDocumentMeta?.storage_path || row?.storage_path || '').trim(),
    };
    const request = queuePublishedDocumentEdit(enrichedRow);
    if (!request) return;
    flyout.setFlyoutOpen(false);
  }, [selectedDocumentMeta]);

  const selectedActionState = useMemo(() => {
    return buildPublishedDocumentActionState({
      row: flyout.selectedDoc || {},
      meta: selectedDocumentMeta || {},
      resolvedUrl: resolvedEventUrl,
      consentSaving: flyout.consentSaving,
    });
  }, [flyout.consentSaving, resolvedEventUrl, selectedDocumentMeta, flyout.selectedDoc]);

  const selectedConsentAction = selectedActionState?.consentAction || { label: 'Solicitar Consentimento', disabled: true, className: 'border-slate-200 bg-slate-100 text-slate-400' };

  const togglePinned = useCallback(async () => {
    const ref = flyout.selectedDoc?.__ref;
    if (!ref) return;
    const nextPinned = !flyout.selectedDoc?.isPinned;

    try {
      await toggleEventPinned(ref, nextPinned);
      flyout.setSelectedDoc((prev) => {
        if (!prev) return prev;
        return { ...prev, isPinned: nextPinned };
      });
      setItems((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return base.map((it) => (String(it?.id || '') === String(flyout.selectedDoc?.id || '') ? { ...it, isPinned: nextPinned } : it));
      });
    } catch (e) {
      void e;
    }
  }, [flyout.selectedDoc]);

  const handleRequestConsent = useCallback(async () => {
    const documentId = String(selectedDocumentMeta?.documentId || '').trim();
    if (!documentId || flyout.consentSaving || selectedConsentAction?.disabled) return;

    flyout.setConsentSaving(true);
    flyout.setConsentFeedback('');
    try {
      const result = await requestDocumentConsent({ documentId, actorName: myName });
      if (!result?.ok) {
        flyout.setConsentFeedback(String(result?.reason || 'Não foi possível solicitar o consentimento.'));
        return;
      }

      const nextMeta = {
        documentId,
        clientId: String(result?.data?.clientId || result?.data?.context?.primaryEntityId || selectedDocumentMeta?.clientId || '').trim(),
        title: String(result?.data?.title || result?.data?.name || result?.data?.fileName || selectedDocumentMeta?.title || '').trim(),
        fileUrl: String(result?.data?.fileUrl || result?.data?.url || result?.data?.downloadUrl || selectedDocumentMeta?.fileUrl || '').trim(),
        url: String(result?.data?.url || selectedDocumentMeta?.url || '').trim(),
        downloadUrl: String(result?.data?.downloadUrl || selectedDocumentMeta?.downloadUrl || '').trim(),
        mirror_url: String(result?.data?.mirror_url || selectedDocumentMeta?.mirror_url || '').trim(),
        storagePath: String(result?.data?.storagePath || selectedDocumentMeta?.storagePath || '').trim(),
        storage_path: String(result?.data?.storage_path || result?.data?._metadata?.storage_path || selectedDocumentMeta?.storage_path || '').trim(),
        storageEngine: result?.data?.storageEngine || selectedDocumentMeta?.storageEngine || null,
        publicationSeriesId: String(result?.data?.publicationSeriesId || selectedDocumentMeta?.publicationSeriesId || documentId).trim(),
        publicationVersion: Math.max(0, Number(result?.data?.publicationVersion || selectedDocumentMeta?.publicationVersion || 0) || 0),
        requiresConsent: Boolean(result?.data?.requiresConsent),
        consentStatus: String(result?.data?.consentStatus || '').trim(),
        status: String(result?.data?.status || '').trim(),
      };

      documentMetaRef.current = {
        ...(documentMetaRef.current || {}),
        [documentId]: nextMeta,
      };
      setDocumentMetaById(documentMetaRef.current || {});
      flyout.setConsentFeedback('Consentimento informado solicitado com sucesso.');
    } catch (e) {
      flyout.setConsentFeedback(String(e?.message || e || 'Falha ao solicitar consentimento.'));
    } finally {
      flyout.setConsentSaving(false);
    }
  }, [flyout.consentSaving, myName, selectedConsentAction?.disabled, selectedDocumentMeta]);

  useEffect(() => {
    let active = true;

    const run = async () => {
      const payload = flyout.selectedDoc?.event?.payload && typeof flyout.selectedDoc.event.payload === 'object' ? flyout.selectedDoc.event.payload : {};
      const directPayloadUrl = String(flyout.selectedDoc?.fileUrl || '').trim();
      const documentId = String(flyout.selectedDoc?.documentId || '').trim();

      setResolvedEventUrl('');

      if (!flyout.flyoutOpen || !flyout.selectedDoc) return;
      if (directPayloadUrl) {
        setResolvedEventUrl(directPayloadUrl);
        return;
      }
      if (!documentId && !selectedDocumentMeta?.storageEngine && !selectedDocumentMeta?.storagePath && !selectedDocumentMeta?.storage_path) return;

      setResolvingEventUrl(true);
      try {
        const resolved = await resolveDocumentUrl({
          storageEngine: flyout.selectedDoc?.storageEngine || selectedDocumentMeta?.storageEngine || null,
          storagePath: String(flyout.selectedDoc?.storagePath || selectedDocumentMeta?.storagePath || '').trim(),
          storage_path: String(flyout.selectedDoc?.storage_path || selectedDocumentMeta?.storage_path || '').trim(),
          fileUrl: directPayloadUrl || String(selectedDocumentMeta?.fileUrl || '').trim(),
          url: String(payload?.url || selectedDocumentMeta?.url || '').trim(),
          downloadUrl: String(payload?.downloadUrl || selectedDocumentMeta?.downloadUrl || '').trim(),
          mirror_url: String(payload?.mirror_url || selectedDocumentMeta?.mirror_url || '').trim(),
        });
        if (!active) return;
        setResolvedEventUrl(String(resolved || '').trim());
      } catch (e) {
        void e;
      } finally {
        if (active) setResolvingEventUrl(false);
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [flyout.flyoutOpen, selectedDocumentMeta, flyout.selectedDoc]);

  const addReply = useCallback(async () => {
    const ref = flyout.selectedDoc?.__ref;
    if (!ref) return;
    const text = String(replyDraft || '').trim();
    if (!text) return;

    const reply = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
      text,
      by: myName,
      at: Date.now(),
    };

    setReplySaving(true);
    try {
      await addEventReply(ref, reply);
      setReplyDraft('');
      flyout.setSelectedDoc((prev) => {
        if (!prev) return prev;
        const list = Array.isArray(prev?.replies) ? prev.replies : [];
        return { ...prev, replies: [...list, reply], repliesCount: list.length + 1 };
      });
      setItems((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return base.map((it) => {
          if (String(it?.id || '') !== String(flyout.selectedDoc?.id || '')) return it;
          const list = Array.isArray(it?.replies) ? it.replies : [];
          return { ...it, replies: [...list, reply] };
        });
      });
    } catch (e) {
      void e;
    } finally {
      setReplySaving(false);
    }
  }, [replyDraft, flyout.selectedDoc, myName]);

  useEffect(() => {
    const ids = new Set();
    (Array.isArray(items) ? items : []).forEach((row) => {
      const normalized = normalizeEvent(row);
      const did = String(normalized?.payload?.documentId || '').trim();
      if (!did) return;
      if (documentMetaRef.current && documentMetaRef.current[did]) return;
      ids.add(did);
    });
    if (ids.size === 0) return;
    if (documentLoadBusyRef.current) return;

    documentLoadBusyRef.current = true;

    (async () => {
      try {
        const list = Array.from(ids);
        await Promise.all(
          list.map(async (did) => {
            try {
              const meta = await fetchDocumentMeta(appId, did);
              if (!meta) return;
              documentMetaRef.current = {
                ...(documentMetaRef.current || {}),
                [did]: meta,
              };
            } catch (e) {
              void e;
            }
          })
        );

        setDocumentMetaById(documentMetaRef.current || {});
      } finally {
        documentLoadBusyRef.current = false;
      }
    })();
  }, [items, normalizeEvent]);

  useEffect(() => {
    const ids = new Set();
    (Array.isArray(gridRows) ? gridRows : []).forEach((r) => {
      const cid = String(r?.clientId || '').trim();
      if (!cid) return;
      if (clientLabelsRef.current && clientLabelsRef.current[cid]) return;
      ids.add(cid);
    });
    if (ids.size === 0) {
      if (gridRows.length > 0) setClientLabelsReady(true);
      return;
    }
    if (clientLoadBusyRef.current) return;

    clientLoadBusyRef.current = true;

    (async () => {
      try {
        const list = Array.from(ids);
        await Promise.all(
          list.map(async (cid) => {
            try {
              const label = await fetchClientMeta(appId, cid);
              if (!label) return;
              clientLabelsRef.current = { ...(clientLabelsRef.current || {}), [cid]: label };
            } catch (e) {
              void e;
            }
          })
        );

        setClientLabels(clientLabelsRef.current || {});
        setClientLabelsReady(true);
      } finally {
        clientLoadBusyRef.current = false;
      }
    })();
  }, [gridRows, documentMetaById]);

  return (
    <div>

      {error ? (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="text-xs font-extrabold uppercase tracking-widest text-red-700">Falha ao carregar</div>
          <div className="mt-2 text-xs text-red-700 whitespace-pre-wrap">{error}</div>
          {indexUrl ? (
            <a
              href={indexUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block text-[11px] font-extrabold uppercase tracking-widest text-red-800 hover:text-red-900 underline"
            >
              Clique aqui para construir o Índice Global no Firebase
            </a>
          ) : null}
        </div>
      ) : null}

      {loading ? <div className="mt-3 text-xs text-slate-500">Carregando...</div> : null}

      {!loading && !error && gridRows.length === 0 && clientLabelsReady ? (
        <div className="mt-3 text-xs text-slate-500">Nenhum evento encontrado.</div>
      ) : null}

      {!loading && !error && gridRows.length > 0 && !clientLabelsReady ? (
        <div className="mt-3 text-xs text-slate-500">Carregando...</div>
      ) : null}

      {!loading && !error && gridRows.length > 0 && clientLabelsReady ? (
        <div className="mt-3 rounded-xl border border-gray-300 bg-white shadow-sm overflow-auto max-h-[72vh]">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
              <tr className="text-[10px] font-extrabold uppercase tracking-widest text-slate-600">
                <th
                  className="w-[220px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === 'CLIENT') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else {
                      setSortKey('CLIENT');
                      setSortDir('asc');
                    }
                  }}
                >
                  Cliente
                </th>
                <th
                  className="w-[260px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === 'WORKSPACE') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else {
                      setSortKey('WORKSPACE');
                      setSortDir('asc');
                    }
                  }}
                >
                  Processo
                </th>
                <th
                  className="w-[150px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === 'DT') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else {
                      setSortKey('DT');
                      setSortDir('desc');
                    }
                  }}
                >
                  Data/Hora
                </th>
                <th
                  className="w-[160px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === 'TYPE') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else {
                      setSortKey('TYPE');
                      setSortDir('asc');
                    }
                  }}
                >
                  Tipo
                </th>
                <th
                  className="w-[200px] px-3 py-2 text-left cursor-pointer select-none"
                  onClick={() => {
                    if (sortKey === 'AUTHOR') setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else {
                      setSortKey('AUTHOR');
                      setSortDir('asc');
                    }
                  }}
                >
                  Autor
                </th>
                <th className="px-3 py-2 text-left">Conteúdo</th>
                <th className="w-[90px] px-3 py-2 text-right">Ações</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {gridRows.map((r) => {
                const rowId = String(r?.id || '');

                const rowBg = r?.isPinned ? 'bg-blue-50/30' : r?.isSystem ? 'bg-slate-50' : 'bg-white';
                const rowClass = `${rowBg} cursor-pointer hover:bg-slate-100`;

                const systemBody = String(r?.fileName || r?.systemText || '').trim();
                const typeDetail = resolveTypeDetail(r?.event);
                const versionState = buildPublicationVersionState({ row: r, latestPublicationVersionBySeries });
                const isCurrentVersion = Boolean(versionState?.isCurrentVersion);
                const versionBadge = String(versionState?.versionBadge || '').trim();

                const clientName =
                  toTitleCasePt(
                    String(r?.clientLabel || clientLabels[String(r?.clientId || '').trim()] || '').trim() ||
                    String(r?.clientId || '').trim()
                  ) ||
                  '—';

                return (
                  <tr
                    key={rowId}
                    className={rowClass}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      flyout.setSelectedDoc(r);
                      setReplyDraft('');
                      flyout.setFlyoutOpen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        flyout.setSelectedDoc(r);
                        setReplyDraft('');
                        flyout.setFlyoutOpen(true);
                      }
                    }}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="text-sm text-slate-600 truncate" title={clientName}>
                        {clientName}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="text-sm text-slate-600 truncate" title={normalizeWorkspaceLabel(r?.workspaceLabel)}>
                        {normalizeWorkspaceLabel(r?.workspaceLabel) || 'Consulta Avulsa'}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top whitespace-nowrap font-mono text-sm text-slate-600">
                      {r?.when || '—'}
                    </td>

                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700">{r?.typeLabel}</span>
                          {versionBadge ? (
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest ${isCurrentVersion ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-500'}`}>
                              {versionBadge}
                            </span>
                          ) : null}
                        </div>
                        {typeDetail ? (
                          <div className="text-sm text-slate-600 truncate" title={typeDetail}>
                            {typeDetail}
                          </div>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top text-sm text-slate-600">
                      <div className="truncate" title={toTitleCasePt(r?.who)}>
                        {toTitleCasePt(r?.who)}
                      </div>
                    </td>

                    <td className="px-3 py-2 align-top">
                      {r?.isSystem ? (
                        systemBody ? (
                          <div className="truncate text-sm text-slate-700" title={systemBody}>
                            {systemBody}
                          </div>
                        ) : (
                          <div className="text-sm text-slate-500">—</div>
                        )
                      ) : r?.safeUserHtml ? (
                        <div
                          className="text-sm text-slate-700 line-clamp-2 max-w-[520px]"
                          dangerouslySetInnerHTML={{ __html: r.safeUserHtml }}
                        />
                      ) : (
                        <div className="text-sm text-slate-500">—</div>
                      )}
                    </td>

                    <td className="px-3 py-2 align-top text-right">
                      <div className="inline-flex items-center justify-end gap-2">
                        {r?.isPinned ? (
                          <span className="inline-flex items-center text-blue-700" title="Fixado">
                            <Pin size={14} />
                          </span>
                        ) : null}
                        {Number(r?.repliesCount || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 text-slate-500" title="Respostas">
                            <MessageSquare size={14} />
                            <span className="text-sm text-slate-600">{r.repliesCount}</span>
                          </span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <FlyoutPanel
        open={flyout.flyoutOpen}
        onClose={() => flyout.setFlyoutOpen(false)}
        title={flyout.selectedDoc?.typeLabel || 'Evento'}
        subtitle={[flyout.selectedDoc?.when ? `Quando: ${flyout.selectedDoc.when}` : '', flyout.selectedDoc?.who ? `Autor: ${flyout.selectedDoc.who}` : '']
          .filter(Boolean)
          .join(' • ')}
        footer={
                <div className="flex flex-col gap-2 w-full">
                  {selectedActionState?.canEditDocument ? (
                    <button
                      type="button"
                      onClick={() => handleEditPublishedDocument(flyout.selectedDoc)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100"
                    >
                      ✏️ Editar Documento
                    </button>
                  ) : null}
                    <button
                      type="button"
                      onClick={() => openPublishedDocument(flyout.selectedDoc)}
                      disabled={resolvingEventUrl || (!String(flyout.selectedDoc?.fileUrl || resolvedEventUrl || '').trim() && !String(flyout.selectedDoc?.documentId || '').trim())}
                      className={
                        'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border ' +
                        ((resolvingEventUrl || (!String(flyout.selectedDoc?.fileUrl || resolvedEventUrl || '').trim() && !String(flyout.selectedDoc?.documentId || '').trim()))
                          ? 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed disabled:opacity-60'
                          : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50')
                      }
                    >
                      {resolvingEventUrl ? 'Carregando documento...' : 'Abrir Documento'}
                    </button>
                    <button
                      type="button"
                      onClick={handleRequestConsent}
                      disabled={flyout.consentSaving || selectedConsentAction.disabled}
                      className={
                        'w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border ' +
                        (flyout.consentSaving
                          ? 'border-amber-200 bg-amber-100 text-amber-800 cursor-wait'
                          : selectedConsentAction.className)
                      }
                      title={selectedConsentAction.label}
                    >
                      {flyout.consentSaving ? 'Solicitando...' : selectedConsentAction.label}
                    </button>
                    <button
                      type="button"
                      onClick={togglePinned}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      aria-label={flyout.selectedDoc?.isPinned ? 'Desafixar' : 'Fixar Nota'}
                      title={flyout.selectedDoc?.isPinned ? 'Desafixar' : 'Fixar Nota'}
                    >
                      <Pin size={16} className={flyout.selectedDoc?.isPinned ? 'fill-blue-600 text-blue-600' : 'text-slate-400'} />
                      {flyout.selectedDoc?.isPinned ? 'Desafixar Nota' : 'Fixar Nota'}
                    </button>
                  {flyout.consentFeedback ? (
                    <div className="text-xs font-semibold text-slate-600 whitespace-pre-wrap">{flyout.consentFeedback}</div>
                  ) : null}
                  <textarea
                    ref={replyTextareaRef}
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(String(e.target.value || ''))}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter') return;
                      if (e.shiftKey) return;
                      if (e.isComposing) return;
                      e.preventDefault();
                      if (replySaving) return;
                      if (!String(replyDraft || '').trim()) return;
                      addReply();
                    }}
                    placeholder="Adicionar comentário a este registro..."
                    className="w-full min-h-[72px] px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:border-blue-300"
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={replySaving || !String(replyDraft || '').trim()}
                      onClick={addReply}
                      className={
                        'inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ' +
                        (replySaving || !String(replyDraft || '').trim()
                          ? 'border-slate-200 bg-slate-100 text-slate-400'
                          : 'border-blue-700 bg-blue-600 text-white hover:bg-blue-700')
                      }
                    >
                      {replySaving ? 'Enviando...' : 'Responder'}
                    </button>
                  </div>
                </div>
        }
      >
                {flyout.selectedDoc?.isSystem ? (
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none">
                      <div className="whitespace-pre-wrap text-sm text-slate-800">
                        {String(flyout.selectedDoc?.fileName || flyout.selectedDoc?.systemText || '').trim() || '—'}
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">Respostas</div>
                      <div className="mt-2">
                        {(Array.isArray(flyout.selectedDoc?.replies) ? flyout.selectedDoc.replies : []).map((rep) => {
                          const rid = String(rep?.id || '');
                          const by = String(rep?.by || '').trim() || '—';
                          const at = rep?.at ? new Date(rep.at).toLocaleString('pt-BR') : '';
                          const text = String(rep?.text || '').trim();
                          if (!rid && !text) return null;
                          return (
                            <div key={rid || at || text} className="bg-slate-50 border border-slate-100 rounded-lg p-3 mt-2">
                              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                {by}{at ? ` • ${at}` : ''}
                              </div>
                              <div className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap">{text || '—'}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : flyout.selectedDoc?.safeUserHtml ? (
                  <div className="p-6">
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: flyout.selectedDoc.safeUserHtml }} />

                    <div className="mt-6">
                      <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-600">Respostas</div>
                      <div className="mt-2">
                        {(Array.isArray(flyout.selectedDoc?.replies) ? flyout.selectedDoc.replies : []).map((rep) => {
                          const rid = String(rep?.id || '');
                          const by = String(rep?.by || '').trim() || '—';
                          const at = rep?.at ? new Date(rep.at).toLocaleString('pt-BR') : '';
                          const text = String(rep?.text || '').trim();
                          if (!rid && !text) return null;
                          return (
                            <div key={rid || at || text} className="bg-slate-50 border border-slate-100 rounded-lg p-3 mt-2">
                              <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">
                                {by}{at ? ` • ${at}` : ''}
                              </div>
                              <div className="mt-1 text-[13px] text-slate-800 whitespace-pre-wrap">{text || '—'}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 text-sm text-slate-500">—</div>
                )}
      </FlyoutPanel>
    </div>
  );
}
